import React, { Fragment, useMemo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Check } from 'lucide-react'
import { StepType, useFormStepContext } from '../FormStepContext'
import useLocaleText from 'components/useLocaleText'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { useCreationText } from '../hooks/useCreationText'
import styles from './StepperProgress.module.css'

const StepperProgress: React.FC<{ neutral?: boolean; compact?: boolean }> = ({ neutral = false, compact = false }) => {
  const { stepStack } = useFormStepContext()
  const t = useCreationText()
  const theme = useSelector((state: RootState) => state.general.theme)

  const accent = neutral ? '#252a2d' : theme?.elementsColor
  const currentStep = stepStack[stepStack.length - 1]

  const task_description = useLocaleText('task_cont')
  const task_sett = useLocaleText('task_sett')

  const steps = useMemo(() => {
    return [
      { number: 1, label: task_description, key: 'description' },
      { number: 2, label: task_sett, key: 'settings' },
    ]
  }, [task_description, task_sett])

  // Determine current step number (1 or 2)
  const currentStepNumber = useMemo(() => {
    if (currentStep === StepType.SUBTASK_TASK_CREATION) {
      return 1
    }
    // Both TASK_SETTINGS and TIME_FRAME_SELECTION are step 2
    if (currentStep === StepType.TASK_SETTINGS || currentStep === StepType.TIME_FRAME_SELECTION) {
      return 2
    }
    return 1
  }, [currentStep])

  const isStepCompleted = (stepNum: number) => {
    return stepNum < currentStepNumber
  }

  const isStepActive = (stepNum: number) => {
    return stepNum === currentStepNumber
  }

  if (compact) {
    return (
      <ol className={styles.compact} aria-label={t.stepsLabel}>
        {[t.subtasks, t.assignStep].map((label, index) => (
          <li key={index} aria-current={currentStepNumber === index + 1 ? 'step' : undefined}>
            <span className={`${styles.number} ${currentStepNumber >= index + 1 ? styles.active : ''}`}>
              {currentStepNumber > index + 1 ? <Check size={12} /> : index + 1}
            </span>
            <span>{label}</span>
          </li>
        ))}
      </ol>
    )
  }

  return (
    <Flex alignItems="center" justifyContent="center" w="100%" py="10px">
      {steps.map((step, index) => (
        <Fragment key={step.key}>
          {/* Step Circle */}
          <Flex flexDir="column" alignItems="center" position="relative">
            <Flex
              w="40px"
              h="40px"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              bg={isStepCompleted(step.number) || isStepActive(step.number) ? accent : '#E2E8F0'}
              color="white"
              fontWeight="bold"
              fontSize="16px"
              transition="all 0.3s ease"
              border={isStepActive(step.number) ? `3px solid ${accent}` : 'none'}
            >
              {isStepCompleted(step.number) ? <Check size={20} strokeWidth={3} /> : <Text>{step.number}</Text>}
            </Flex>

            {/* Step Label */}
            <Text
              mt="8px"
              fontSize="12px"
              fontWeight={isStepActive(step.number) ? 'bold' : 'normal'}
              color={isStepActive(step.number) ? accent : '#718096'}
              textAlign="center"
              maxW="80px"
            >
              {step.label}
            </Text>
          </Flex>

          {/* Connector Line */}
          {index < steps.length - 1 && (
            <Box
              h="3px"
              w="120px"
              bg={isStepCompleted(step.number + 1) || isStepActive(step.number + 1) ? accent : '#E2E8F0'}
              mx="8px"
              mb="28px"
              transition="all 0.3s ease"
            />
          )}
        </Fragment>
      ))}
    </Flex>
  )
}

export default StepperProgress
